"use client";
import {
	AlertCircle,
	RefreshCcw,
	Copy,
	Plug,
	Brain,
	GlobeIcon,
	PenLine,
	Pencil,
	Check,
	X,
	Compass,
	Code2,
	ImageIcon,
} from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Actions, Action } from "@/components/ai-elements/actions";
import {
	Conversation,
	ConversationContent,
} from "@/components/ai-elements/conversation";
import { Image } from "@/components/ai-elements/image";
import { Loader } from "@/components/ai-elements/loader";
import { Message, MessageContent } from "@/components/ai-elements/message";
import {
	PromptInput,
	PromptInputActionAddAttachments,
	PromptInputActionMenu,
	PromptInputActionMenuContent,
	PromptInputActionMenuTrigger,
	PromptInputAttachment,
	PromptInputAttachments,
	PromptInputBody,
	PromptInputButton,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputToolbar,
	PromptInputTools,
} from "@/components/ai-elements/prompt-input";
import {
	Reasoning,
	ReasoningContent,
	ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import { Response } from "@/components/ai-elements/response";
import {
	Source,
	Sources,
	SourcesContent,
	SourcesTrigger,
} from "@/components/ai-elements/sources";
import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import {
	Tool,
	ToolContent,
	ToolHeader,
	ToolInput,
	ToolOutput,
} from "@/components/ai-elements/tool";
import { ConnectorsDialog } from "@/components/connectors/connectors-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ImageZoom } from "@/components/ui/image-zoom";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { parseImagePartToDataUrl } from "@/lib/image-utils";

import type { UIMessage, ChatRequestOptions, ChatStatus } from "ai";

interface ChatUIProps {
	messages: UIMessage[];
	supportsImages: boolean;
	supportsImageGen: boolean;
	sendMessage: (
		message: UIMessage,
		options?: ChatRequestOptions,
	) => Promise<void>;
	selectedModel: string;
	text: string;
	setText: (text: string) => void;
	status: ChatStatus;
	stop: () => void;
	regenerate: () => void;
	reasoningEffort: "" | "minimal" | "low" | "medium" | "high";
	setReasoningEffort: (
		value: "" | "minimal" | "low" | "medium" | "high",
	) => void;
	supportsReasoning: boolean;
	imageAspectRatio:
		| "auto"
		| "1:1"
		| "9:16"
		| "3:4"
		| "4:3"
		| "3:2"
		| "2:3"
		| "5:4"
		| "4:5"
		| "21:9";
	setImageAspectRatio: (
		value:
			| "auto"
			| "1:1"
			| "9:16"
			| "3:4"
			| "4:3"
			| "3:2"
			| "2:3"
			| "5:4"
			| "4:5"
			| "21:9",
	) => void;
	imageSize: "1K" | "2K" | "4K";
	setImageSize: (value: "1K" | "2K" | "4K") => void;
	alibabaImageSize: string;
	setAlibabaImageSize: (value: string) => void;
	supportsWebSearch: boolean;
	webSearchEnabled: boolean;
	setWebSearchEnabled: (value: boolean) => void;
	onUserMessage?: (
		content: string,
		images?: Array<{
			type: "image_url";
			image_url: {
				url: string;
			};
		}>,
	) => Promise<void>;
	setMessages?: (messages: UIMessage[]) => void;
	isLoading?: boolean;
	error?: string | null;
}

const suggestions = [
	"Help me brainstorm unique names for a tech startup in the fintech space",
	"Explain the benefits of serverless architecture in simple terms",
	"Create a step-by-step guide for setting up a CI/CD pipeline",
	"Write a professional email to reschedule a client meeting",
	"Generate ideas for a personal blog about web development",
	"Suggest strategies to improve team collaboration in remote work",
	"Design a study plan for learning machine learning fundamentals",
	"Write a product description for a minimalist desk organizer",
	"Explain the difference between REST and GraphQL APIs",
	"Create a content calendar for a SaaS company's social media",
];

const heroSuggestionGroups = {
	Create: suggestions,
	Explore: [
		"What are the latest developments in edge computing?",
		"Explain recent advancements in renewable energy technology",
		"Discover emerging trends in user experience design",
		"Find innovative approaches to database optimization",
	],
	Code: [
		"Write a TypeScript function to validate email addresses",
		"Create a custom React hook for managing form state",
		"Explain the concept of middleware in Express.js",
		"Show best practices for handling API errors in JavaScript",
	],
	"Image gen": [
		"Generate an image of a modern minimalist workspace",
		"Create a vibrant abstract pattern with geometric shapes",
		"Design a concept art of an eco-friendly smart home",
	],
};

const heroGroupIcons: Record<string, React.ReactNode> = {
	Create: <PenLine className="size-3.5" />,
	Explore: <Compass className="size-3.5" />,
	Code: <Code2 className="size-3.5" />,
	"Image gen": <ImageIcon className="size-3.5" />,
};

export const ChatUI = ({
	messages,
	supportsImages,
	supportsImageGen,
	sendMessage,
	selectedModel,
	text,
	setText,
	status,
	stop,
	regenerate,
	reasoningEffort,
	setReasoningEffort,
	supportsReasoning,
	imageAspectRatio,
	setImageAspectRatio,
	imageSize,
	setImageSize,
	alibabaImageSize,
	setAlibabaImageSize,
	supportsWebSearch,
	webSearchEnabled,
	setWebSearchEnabled,
	onUserMessage,
	setMessages,
	isLoading = false,
	error = null,
}: ChatUIProps) => {
	// Check if the model uses WIDTHxHEIGHT format (Alibaba or ZAI)
	const usesPixelDimensions =
		selectedModel.toLowerCase().includes("alibaba") ||
		selectedModel.toLowerCase().includes("qwen-image") ||
		selectedModel.toLowerCase().includes("zai") ||
		selectedModel.toLowerCase().includes("cogview");

	const [activeGroup, setActiveGroup] =
		useState<keyof typeof heroSuggestionGroups>("Create");
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const handlePromptSubmit = async (
		textContent: string,
		files?: Array<{
			url?: string | null;
			mediaType?: string | null;
			filename?: string | null;
		}>,
	) => {
		if (isLoading || status === "streaming") {
			return;
		}

		try {
			const content = textContent ?? "";
			if (!content.trim() && !files?.length) {
				return;
			}

			// Check if user is trying to send images to a non-vision model
			const hasImageFiles = files?.some((f) =>
				f.mediaType?.startsWith("image/"),
			);
			if (hasImageFiles && !supportsImages) {
				toast.error("Switch to a vision model to send images", {
					duration: 5000,
				});
				return;
			}

			setText(""); // Clear input immediately

			const parts: any[] = [];

			if (content.trim()) {
				parts.push({ type: "text", text: content });
			}

			// Attach user images/files as AI SDK "file" parts so vision /
			// image-generation models can actually consume them.
			if (supportsImages && files?.length) {
				for (const file of files) {
					if (file.mediaType?.startsWith("image/") && file.url) {
						parts.push({
							type: "file",
							url: file.url,
							mediaType: file.mediaType,
							name: file.filename,
						});
					}
				}
			}

			if (parts.length === 0) {
				return;
			}

			// CRITICAL FIX: Save to database FIRST, before sending to AI
			// This ensures the chat exists before the AI response comes back
			if (onUserMessage && content.trim()) {
				try {
					await onUserMessage(content);
				} catch (error) {
					toast.error(`Failed to save message: ${error}`);
					return; // Don't proceed if we can't save
				}
			}

			// Now call sendMessage which will handle the AI request
			await sendMessage(
				{
					id: crypto.randomUUID(),
					role: "user",
					parts,
				},
				{
					body: {
						model: selectedModel,
					},
				},
			);
		} catch {
			toast.error("Could not send message.");
		}
	};

	const [editingId, setEditingId] = useState<string | null>(null);
	const [editingText, setEditingText] = useState("");

	const canEdit = !!setMessages && !!onUserMessage;

	const startEditing = (messageId: string, currentText: string) => {
		setEditingId(messageId);
		setEditingText(currentText);
	};

	const cancelEditing = () => {
		setEditingId(null);
		setEditingText("");
	};

	const submitEdit = async (messageId: string) => {
		const newText = editingText.trim();
		if (!newText || !setMessages) {
			cancelEditing();
			return;
		}
		const index = messages.findIndex((m) => m.id === messageId);
		if (index === -1) {
			cancelEditing();
			return;
		}
		// Truncate the thread to just before the edited message, then resend
		// the edited text as a fresh turn (persists + streams a new response).
		setMessages(messages.slice(0, index));
		cancelEditing();
		await handlePromptSubmit(newText);
	};

	const followUpSuggestions = [
		"Explain in more detail",
		"Give a concrete example",
		"Summarize the key points",
		"What are the trade-offs?",
	];

	const lastMessage = messages.at(-1);
	const showFollowUps =
		canEdit &&
		!!lastMessage &&
		lastMessage.role === "assistant" &&
		status !== "streaming" &&
		status !== "submitted" &&
		messages.length > 0;

	return (
		<div className="flex flex-col h-full min-h-0">
			<div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0">
				<Conversation>
					<ConversationContent>
						{isLoading && messages.length === 0 ? (
							<div className="flex items-center justify-center h-full">
								<Loader />
							</div>
						) : messages.length === 0 ? (
							<div className="max-w-2xl mx-auto py-16 px-4">
								<div className="mb-8 text-center">
									<h2 className="text-2xl font-semibold tracking-tight mb-2">
										How can I help you?
									</h2>
									<p className="text-sm text-muted-foreground">
										Choose a category or type your own message below
									</p>
								</div>
								<div className="mb-8 flex justify-center gap-2 flex-wrap">
									{Object.keys(heroSuggestionGroups).map((key) => (
										<Button
											key={key}
											size="sm"
											variant={activeGroup === key ? "default" : "outline"}
											onClick={() =>
												setActiveGroup(key as keyof typeof heroSuggestionGroups)
											}
											className="rounded-full px-4 gap-1.5"
										>
											{heroGroupIcons[key]}
											{key}
										</Button>
									))}
								</div>
								{activeGroup === "Image gen" && !supportsImageGen ? (
									<div className="text-center text-sm text-muted-foreground py-8">
										Select an image generation model to use this feature.
									</div>
								) : (
									<div className="space-y-2.5">
										{heroSuggestionGroups[activeGroup].slice(0, 5).map((s) => (
											<button
												key={s}
												type="button"
												onClick={() => {
													void handlePromptSubmit(s);
												}}
												className="w-full rounded-2xl border px-5 py-3.5 text-left text-sm hover:bg-accent transition-all duration-150 hover:shadow-sm"
											>
												{s}
											</button>
										))}
									</div>
								)}
							</div>
						) : (
							messages.map((m, messageIndex) => {
								const isLastMessage = messageIndex === messages.length - 1;

								if (m.role === "assistant") {
									let textContent = m.parts
										.filter((p) => p.type === "text")
										.map((p) => p.text)
										.join("");
									const toolParts = m.parts.filter(
										(p) => p.type === "dynamic-tool",
									);
									// Combine all image parts (both image_url and file types)
									const imageParts = m.parts.filter(
										(p: any) =>
											(p.type === "image_url" && p.image_url?.url) ||
											(p.type === "file" && p.mediaType?.startsWith("image/")),
									);
									let reasoningContent = m.parts
										.filter((p) => p.type === "reasoning")
										.map((p) => p.text)
										.join("");

									// Extract <think> tags from textContent (for models like minimax)
									const thinkTagMatch = textContent.match(
										/<think>([\s\S]*?)<\/think>/,
									);
									if (thinkTagMatch && !reasoningContent) {
										reasoningContent = thinkTagMatch[1].trim();
										// Remove <think> tags from displayed content
										textContent = textContent
											.replace(/<think>[\s\S]*?<\/think>\s*/g, "")
											.trim();
									}

									const sourceParts = m.parts.filter(
										(p) => p.type === "source-url",
									);

									return (
										<div key={m.id}>
											{reasoningContent ? (
												<Reasoning
													className="w-full"
													isStreaming={
														status === "streaming" &&
														m.id === messages.at(-1)?.id
													}
												>
													<ReasoningTrigger />
													<ReasoningContent>
														{reasoningContent}
													</ReasoningContent>
												</Reasoning>
											) : null}

											{toolParts.map((tool) => (
												<Tool key={tool.toolCallId}>
													<ToolHeader
														type={tool.type as `tool-${string}`}
														state={tool.state}
													/>
													<ToolContent>
														<ToolInput input={tool.input} />
														<ToolOutput
															errorText={tool.errorText}
															output={tool.output}
														/>
													</ToolContent>
												</Tool>
											))}

											{/* Then assistant text */}
											{textContent ? <Response>{textContent}</Response> : null}

											{/* Images after text */}
											{imageParts.length > 0 ? (
												<div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
													{imageParts.map((part: any, idx: number) => {
														const { base64Only, mediaType } =
															parseImagePartToDataUrl(part);
														if (!base64Only) {
															return null;
														}
														return (
															<ImageZoom key={idx}>
																<Image
																	base64={base64Only}
																	mediaType={mediaType}
																	alt={part.name || "Generated image"}
																	className="h-[400px] aspect-auto border rounded-xl object-cover"
																/>
															</ImageZoom>
														);
													})}
												</div>
											) : isLastMessage && status === "streaming" ? (
												<div className="mt-3">
													<Loader />
												</div>
											) : null}

											{/* Sources at the bottom */}
											{sourceParts.length > 0 ? (
												<Sources>
													<SourcesTrigger count={sourceParts.length} />
													{sourceParts.map((part, i) => (
														<SourcesContent key={`${m.id}-${i}`}>
															<Source href={part.url} title={part.url} />
														</SourcesContent>
													))}
												</Sources>
											) : null}

											{isLastMessage && (
												<Actions className="mt-2">
													<Action
														onClick={() => regenerate()}
														label="Retry"
														tooltip="Regenerate response"
													>
														<RefreshCcw className="size-3" />
													</Action>
													<Action
														onClick={async () => {
															try {
																await navigator.clipboard.writeText(
																	textContent,
																);
																toast.success("Copied to clipboard");
															} catch {
																toast.error("Failed to copy to clipboard");
															}
														}}
														label="Copy"
														tooltip="Copy to clipboard"
													>
														<Copy className="size-3" />
													</Action>
												</Actions>
											)}
										</div>
									);
								} else {
									// User messages: show text plus any attached images
									const textParts = m.parts
										.filter((p) => p.type === "text")
										.map((p) => p.text);

									const imageParts = (m.parts as any[]).filter(
										(p: any) =>
											p.type === "file" && p.mediaType?.startsWith("image/"),
									);

									const userText = textParts.join("\n");
									const isEditing = editingId === m.id;

									if (isEditing) {
										return (
											<div key={m.id} className="flex justify-end">
												<div className="w-full max-w-[80%] rounded-2xl border bg-muted/40 p-2">
													<Textarea
														value={editingText}
														onChange={(e) => setEditingText(e.target.value)}
														onKeyDown={(e) => {
															if (
																(e.metaKey || e.ctrlKey) &&
																e.key === "Enter"
															) {
																e.preventDefault();
																void submitEdit(m.id);
															}
															if (e.key === "Escape") {
																e.preventDefault();
																cancelEditing();
															}
														}}
														autoFocus
														className="min-h-[72px] resize-none border-none bg-transparent focus-visible:ring-0"
													/>
													<div className="mt-1 flex items-center justify-end gap-2">
														<Button
															size="sm"
															variant="ghost"
															onClick={cancelEditing}
														>
															<X className="mr-1 size-3" />
															Cancel
														</Button>
														<Button
															size="sm"
															onClick={() => void submitEdit(m.id)}
															disabled={!editingText.trim()}
														>
															<Check className="mr-1 size-3" />
															Send
														</Button>
													</div>
												</div>
											</div>
										);
									}

									return (
										<div key={m.id} className="group flex flex-col items-end">
											<Message from={m.role}>
												<MessageContent variant="flat">
													{textParts.map((t, idx) => (
														<div key={idx}>{t}</div>
													))}
													{imageParts.length > 0 && (
														<div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
															{imageParts.map((part: any, idx: number) => {
																const { base64Only, mediaType } =
																	parseImagePartToDataUrl(part);
																if (!base64Only) {
																	return null;
																}
																return (
																	<ImageZoom key={idx}>
																		<Image
																			base64={base64Only}
																			mediaType={mediaType}
																			alt={part.name || "Uploaded image"}
																			className="h-[300px] aspect-auto border rounded-xl object-cover"
																		/>
																	</ImageZoom>
																);
															})}
														</div>
													)}
												</MessageContent>
												{isLastMessage &&
													(status === "submitted" ||
														status === "streaming") && <Loader />}
											</Message>
											{canEdit && userText.trim() && (
												<Actions className="mt-1 opacity-0 transition-opacity group-hover:opacity-100">
													<Action
														onClick={() => startEditing(m.id, userText)}
														label="Edit"
														tooltip="Edit message"
													>
														<Pencil className="size-3" />
													</Action>
												</Actions>
											)}
										</div>
									);
								}
							})
						)}
						{showFollowUps && (
							<Suggestions className="mt-3 px-1">
								{followUpSuggestions.map((s) => (
									<Suggestion
										key={s}
										suggestion={s}
										onClick={(value) => {
											void handlePromptSubmit(value);
										}}
									/>
								))}
							</Suggestions>
						)}
					</ConversationContent>
				</Conversation>
			</div>

			<div className="shrink-0 px-4 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2">
				{error && (
					<Alert variant="destructive" className="mb-3 rounded-xl">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}
				<PromptInput
					accept={supportsImages ? "image/*" : undefined}
					multiple
					globalDrop
					aria-disabled={isLoading || status === "streaming"}
					onSubmit={async (message) => {
						await handlePromptSubmit(message.text ?? "", message.files);
					}}
				>
					<PromptInputBody>
						<PromptInputAttachments>
							{(attachment) => <PromptInputAttachment data={attachment} />}
						</PromptInputAttachments>
						<PromptInputTextarea
							ref={textareaRef}
							value={text}
							onChange={(e) => setText(e.currentTarget.value)}
							placeholder="How can I help you today?"
						/>
					</PromptInputBody>
					<PromptInputToolbar>
						<PromptInputTools>
							<PromptInputActionMenu>
								<PromptInputActionMenuTrigger />
								<PromptInputActionMenuContent>
									<PromptInputActionAddAttachments />
								</PromptInputActionMenuContent>
							</PromptInputActionMenu>
							<PromptInputSpeechButton
								onTranscriptionChange={setText}
								textareaRef={textareaRef}
							/>
							{supportsWebSearch && (
								<PromptInputButton
									variant={webSearchEnabled ? "default" : "ghost"}
									onClick={() => setWebSearchEnabled(!webSearchEnabled)}
								>
									<GlobeIcon size={16} />
								</PromptInputButton>
							)}
							<ConnectorsDialog
								trigger={
									<PromptInputButton variant="ghost">
										<Plug size={16} />
										<span>MCPs</span>
									</PromptInputButton>
								}
							/>
						</PromptInputTools>
						<div className="flex items-center gap-2">
							{supportsReasoning && (
								<Select
									value={reasoningEffort ? reasoningEffort : "off"}
									onValueChange={(val) =>
										setReasoningEffort(
											val === "off"
												? ""
												: ((val as "minimal" | "low" | "medium" | "high") ??
														""),
										)
									}
								>
									<SelectTrigger size="sm" className="min-w-[120px]">
										<Brain size={16} />
										<SelectValue placeholder="Reasoning" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="off">Auto</SelectItem>
										{selectedModel.includes("gpt-5") && (
											<SelectItem value="minimal">Minimal</SelectItem>
										)}
										<SelectItem value="low">Low</SelectItem>
										<SelectItem value="medium">Medium</SelectItem>
										<SelectItem value="high">High</SelectItem>
									</SelectContent>
								</Select>
							)}
							{supportsImageGen && !usesPixelDimensions && (
								<>
									<Select
										value={imageAspectRatio}
										onValueChange={(val) =>
											setImageAspectRatio(
												val as
													| "auto"
													| "1:1"
													| "9:16"
													| "3:4"
													| "4:3"
													| "3:2"
													| "2:3"
													| "5:4"
													| "4:5"
													| "21:9",
											)
										}
									>
										<SelectTrigger size="sm" className="min-w-[110px]">
											<SelectValue placeholder="Aspect ratio" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="auto">Auto</SelectItem>
											<SelectItem value="1:1">1:1</SelectItem>
											<SelectItem value="9:16">9:16</SelectItem>
											<SelectItem value="3:4">3:4</SelectItem>
											<SelectItem value="4:3">4:3</SelectItem>
											<SelectItem value="3:2">3:2</SelectItem>
											<SelectItem value="2:3">2:3</SelectItem>
											<SelectItem value="5:4">5:4</SelectItem>
											<SelectItem value="4:5">4:5</SelectItem>
											<SelectItem value="21:9">21:9</SelectItem>
										</SelectContent>
									</Select>
									<Select
										value={imageSize}
										onValueChange={(val) =>
											setImageSize(val as "1K" | "2K" | "4K")
										}
									>
										<SelectTrigger size="sm" className="min-w-[80px]">
											<SelectValue placeholder="Resolution" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="1K">1K</SelectItem>
											<SelectItem value="2K">2K</SelectItem>
											<SelectItem value="4K">4K</SelectItem>
										</SelectContent>
									</Select>
								</>
							)}
							{supportsImageGen && usesPixelDimensions && (
								<Select
									value={alibabaImageSize}
									onValueChange={setAlibabaImageSize}
								>
									<SelectTrigger size="sm" className="min-w-[130px]">
										<SelectValue placeholder="Image Size" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="1024x1024">1024x1024</SelectItem>
										<SelectItem value="720x1280">720x1280</SelectItem>
										<SelectItem value="1280x720">1280x720</SelectItem>
										<SelectItem value="1024x1536">1024x1536</SelectItem>
										<SelectItem value="1536x1024">1536x1024</SelectItem>
										<SelectItem value="2048x1024">2048x1024</SelectItem>
										<SelectItem value="1024x2048">1024x2048</SelectItem>
									</SelectContent>
								</Select>
							)}
							{status === "streaming" ? (
								<PromptInputButton onClick={() => stop()} variant="ghost">
									Stop
								</PromptInputButton>
							) : null}
							<PromptInputSubmit
								status={status === "streaming" ? "streaming" : "ready"}
								disabled={isLoading}
							/>
						</div>
					</PromptInputToolbar>
				</PromptInput>
			</div>
		</div>
	);
};
